import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, LogOut, SearchCheck } from 'lucide-react'
import { useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router'

import { ErrorDeLaApi, consulta, pedir } from '../api/nucleo'
import type { Sitio } from '../api/tipos'
import { Aviso, Boton, Esqueleto, Pastilla, Tarjeta, Titulo } from '../componentes/ui'
import { nombreDeLaMarca } from '../marca/marca'
import type { Contexto } from './Modulo'

/**
 * «Google Search Console» de una web (CLAUDE.md §10): conectar la web con la Search Console de
 * su dueño, con su cuenta de Google, y ver qué búsquedas la traen. Todo lo hace el núcleo
 * (`/api/conexiones-de-google/…`): al volver de Google verifica la web, la da de alta y le manda
 * el sitemap. Aquí se pulsa el botón y se enseña lo que el núcleo dice.
 */

type Estado = {
  disponible: boolean
  conexion: null | { estado: 'conectada' | 'pendiente' | 'vinculada' | 'sinPermiso' | 'error'; cuenta: null | string; propiedad: null | string; error: null | string; vinculadaEn: null | string }
  datos: null | {
    desde: string
    hasta: string
    clics: number
    impresiones: number
    ctr: number
    posicion: number
    consultas: Array<{ texto: string; clics: number; impresiones: number; posicion: number }>
    paginas: Array<{ url: string; clics: number; impresiones: number }>
  }
  errorDeDatos: null | string
}

const ESTADO: Record<NonNullable<Estado['conexion']>['estado'], { clave: 'construyendo' | 'fallida' | 'publicado' | 'sin-publicar'; texto: string }> = {
  conectada: { clave: 'construyendo', texto: 'Conectando' },
  pendiente: { clave: 'sin-publicar', texto: 'Esperando a que Google vea tu web' },
  vinculada: { clave: 'publicado', texto: 'Conectada y verificada' },
  sinPermiso: { clave: 'fallida', texto: 'Hay que volver a conectar' },
  error: { clave: 'fallida', texto: 'Hay un problema' },
}

const RESULTADO: Record<string, { tono: 'error' | 'info' | 'ok'; texto: string }> = {
  vinculada: { tono: 'ok', texto: 'Listo: tu web está verificada en Google Search Console y le hemos enviado el mapa del sitio. Los datos tardan unos días en aparecer.' },
  pendiente: { tono: 'info', texto: 'Conectado con Google. Falta que Google vea tu web: en cuanto la publiques lo verificamos solos.' },
  cancelada: { tono: 'info', texto: 'No se ha conectado: se canceló en Google.' },
}

const numero = (n: number) => n.toLocaleString('es')
const fecha = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short' })

export const SearchConsole = () => {
  const { permisos, sitio } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (permisos.cargando) return <Esqueleto />
  if (!permisos.tiene('seo.sitio')) {
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Google Search Console</Titulo>
        <Aviso tono="info">La conexión con Google la gestiona quien lleva el SEO de la web. Si hace falta, pídeselo.</Aviso>
      </>
    )
  }
  return <Pantalla key={sitio.id} sitio={sitio} />
}

const Pantalla = ({ sitio }: { sitio: Sitio }) => {
  const cliente = useQueryClient()
  const [parametros, setParametros] = useSearchParams()
  const [enviando, setEnviando] = useState<'conectar' | 'desconectar' | null>(null)
  const [error, setError] = useState<null | string>(null)
  const { data, isPending } = useQuery({
    queryKey: ['search-console', sitio.id],
    queryFn: () => pedir<Estado>(`/conexiones-de-google/estado${consulta({ sitio: sitio.id })}`),
  })

  const resultado = parametros.get('resultado')
  const avisoDeVuelta = resultado ? (RESULTADO[resultado] ?? { tono: 'error' as const, texto: parametros.get('mensaje') || 'No se ha podido conectar con Google. Vuelve a intentarlo.' }) : null

  const conectar = async () => {
    setEnviando('conectar')
    setError(null)
    try {
      const { url } = await pedir<{ url: string }>('/conexiones-de-google/conectar', { metodo: 'POST', cuerpo: { sitio: sitio.id } })
      window.location.assign(url)
    } catch (e) {
      setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido empezar la conexión. Vuelve a intentarlo.')
      setEnviando(null)
    }
  }
  const desconectar = async () => {
    if (!window.confirm(`¿Desconectar tu web de Google Search Console? ${nombreDeLaMarca()} dejará de ver tus datos de búsqueda. Tu propiedad sigue en tu cuenta de Google.`)) return
    setEnviando('desconectar')
    setError(null)
    try {
      await pedir('/conexiones-de-google/desconectar', { metodo: 'POST', cuerpo: { sitio: sitio.id } })
      setParametros({})
      await cliente.invalidateQueries({ queryKey: ['search-console', sitio.id] })
    } catch (e) {
      setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido desconectar. Vuelve a intentarlo.')
    } finally {
      setEnviando(null)
    }
  }

  return (
    <>
      <Titulo descripcion="Conecta tu web con tu cuenta de Google: la verificamos, la damos de alta y te enseñamos qué búsquedas te traen visitas." rotulo={sitio.nombre}>
        Google Search Console
      </Titulo>
      <div className="flex flex-col gap-5">
        {avisoDeVuelta && <Aviso tono={avisoDeVuelta.tono}>{avisoDeVuelta.texto}</Aviso>}
        {error && <Aviso tono="error">{error}</Aviso>}
        {isPending || !data ? (
          <Esqueleto />
        ) : !data.disponible ? (
          <Aviso tono="info">La conexión con Google todavía no está disponible. Te avisaremos cuando lo esté.</Aviso>
        ) : !data.conexion ? (
          <Tarjeta>
            <div className="flex flex-col gap-3">
              <p className="text-sm">
                Pulsa el botón, elige tu cuenta de Google y acepta los permisos. Nosotros verificamos tu web en Search Console, la damos de alta y le mandamos el mapa del sitio. No tienes que copiar ningún código.
              </p>
              <div>
                <Boton cargando={enviando === 'conectar'} icono={<SearchCheck />} onClick={conectar} tipo="primario">
                  Conectar con Google
                </Boton>
              </div>
            </div>
          </Tarjeta>
        ) : (
          <>
            <Tarjeta>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Pastilla clave={ESTADO[data.conexion.estado].clave}>{ESTADO[data.conexion.estado].texto}</Pastilla>
                    {data.conexion.cuenta && <span className="text-sm text-tenue">con {data.conexion.cuenta}</span>}
                  </div>
                  {data.conexion.propiedad && <p className="mt-2 break-all text-sm">{data.conexion.propiedad}</p>}
                  {data.conexion.error && <p className="mt-2 text-sm text-tenue">{data.conexion.error}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(data.conexion.estado === 'sinPermiso' || data.conexion.estado === 'error') && (
                    <Boton cargando={enviando === 'conectar'} icono={<SearchCheck />} onClick={conectar} pequeno tipo="primario">
                      Volver a conectar
                    </Boton>
                  )}
                  {data.conexion.propiedad && (
                    <a className="inline-flex h-8 items-center gap-1.5 rounded-boton border border-linea px-3 text-[13px] font-medium" href={`https://search.google.com/search-console?resource_id=${encodeURIComponent(data.conexion.propiedad)}`} rel="noreferrer" target="_blank">
                      <ExternalLink className="size-3.5" /> Abrir en Google
                    </a>
                  )}
                  <Boton cargando={enviando === 'desconectar'} icono={<LogOut />} onClick={desconectar} pequeno tipo="discreto">
                    Desconectar
                  </Boton>
                </div>
              </div>
            </Tarjeta>
            {data.errorDeDatos && <Aviso tono="aviso">{data.errorDeDatos}</Aviso>}
            {data.datos && <Datos datos={data.datos} />}
          </>
        )}
      </div>
    </>
  )
}

const Datos = ({ datos }: { datos: NonNullable<Estado['datos']> }) => (
  <>
    <Tarjeta>
      <p className="mb-3 text-xs text-tenue">
        Del {fecha(datos.desde)} al {fecha(datos.hasta)} (Google tarda un par de días en tener los últimos)
      </p>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          ['Clics', numero(datos.clics)],
          ['Veces que salió', numero(datos.impresiones)],
          ['Clics por cada 100', (datos.ctr * 100).toLocaleString('es', { maximumFractionDigits: 1 })],
          ['Posición media', datos.posicion ? datos.posicion.toLocaleString('es', { maximumFractionDigits: 1 }) : '—'],
        ].map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="text-xs text-tenue">{rotulo}</dt>
            <dd className="font-display text-2xl font-semibold">{valor}</dd>
          </div>
        ))}
      </dl>
    </Tarjeta>
    <div className="grid gap-5 lg:grid-cols-2">
      <Tarjeta>
        <h3 className="mb-2 font-display text-base font-semibold">Lo que buscan para llegar a tu web</h3>
        {datos.consultas.length === 0 ? (
          <p className="text-sm text-tenue">Todavía no hay búsquedas. Aparecen cuando Google empieza a enseñar tu web.</p>
        ) : (
          <ul className="divide-y divide-linea text-sm">
            {datos.consultas.map((c) => (
              <li className="flex items-baseline justify-between gap-3 py-1.5" key={c.texto}>
                <span className="min-w-0 break-words">{c.texto}</span>
                <span className="shrink-0 text-tenue">{numero(c.clics)} clics</span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
      <Tarjeta>
        <h3 className="mb-2 font-display text-base font-semibold">Las páginas que más visitas traen</h3>
        {datos.paginas.length === 0 ? (
          <p className="text-sm text-tenue">Todavía no hay datos.</p>
        ) : (
          <ul className="divide-y divide-linea text-sm">
            {datos.paginas.map((p) => (
              <li className="flex items-baseline justify-between gap-3 py-1.5" key={p.url}>
                <span className="min-w-0 break-all">{p.url.replace(/^https?:\/\/[^/]+/, '') || '/'}</span>
                <span className="shrink-0 text-tenue">{numero(p.clics)} clics</span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  </>
)
